import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from '@emotion/styled';

import Header from 'components/Common/Header';
import Tab from 'components/Common/Tab';
import TextInput from 'components/Common/TextInput';
import Table from 'components/Common/StyledTable';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useNavigate } from 'react-router-dom';
import routes from 'constants/routes';
import Button from 'components/Common/Button';
import { RootState, store } from 'redux/store';
import MuiLoader from 'components/Common/Loader';
import {
    clearParishMembers,
    fetchParishMembers,
    getMemberInvitations,
    revokeMemberInvitationThunk,
} from 'redux/slices/community/parishMemberSlice';
import { selectSelectedParishId, selectHasPortalStaffAccess } from 'redux/selectors/parishContext';
import { updateTableState } from 'redux/slices/common/appSlice';
import { Alert, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import DeleteModal from 'components/Common/DeleteModal';
import { debounce } from 'utils/helpers';
import InviteMemberModal from 'components/Community/InviteMemberModal';
import type { MemberInvitation } from 'services/memberInvitationsApi';

const StyledContainer = styled.div({
    backgroundColor: '#F2F2F6',
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
});

const StyledActionContainer = styled.div({
    marginBottom: 16,
    padding: 22,
});

const StyledInnerContainer = styled.div({
    backgroundColor: 'white',
    padding: 1,
    marginTop: 10,
});

type TabValue = 'parishmembers' | 'invitations';

const columns = [
    { id: 'firstName', label: 'First Name' },
    { id: 'lastName', label: 'Last Name' },
    { id: 'username', label: 'Email' },
    { id: 'status', label: 'Status', render: ({ value }: { value: string }) => value || 'Member' },
];

const baseInvitationColumns = [
    { id: 'email', label: 'Email' },
    { id: 'name', label: 'Name' },
    { id: 'status', label: 'Status' },
];

function formatInvitationRow(inv: MemberInvitation) {
    const name = [inv.firstName, inv.lastName].filter(Boolean).join(' ');
    return { id: inv.id, email: inv.email, name, status: inv.status };
}

const Community: React.FC = () => {
    const [tab, setTab] = useState<TabValue>('parishmembers');
    const navigate = useNavigate();
    const dispatch = useDispatch<typeof store.dispatch>();
    const [searchText, setSearchText] = useState<string>('');
    const [inviteMemberModal, setInviteMemberModal] = useState(false);
    const [revokeTarget, setRevokeTarget] = useState<{ id: string; email: string } | null>(null);
    const [revoking, setRevoking] = useState(false);
    const parishId = useSelector(selectSelectedParishId);
    // Reuses the portal's own staff gate (effective role for the selected parish ≥ CLERK) —
    // matches the backend's @RequireParishRole('CLERK') on the member-invitations endpoints,
    // and stays in sync if MIN_PORTAL_STAFF_ROLE ever changes.
    const canManageMembers = useSelector(selectHasPortalStaffAccess);
    const { parishList, loading, error, total, invitations, invitationsTotal, invitationsLoading } = useSelector(
        (state: RootState) => state.community.perishMembers
    );
    const { pageNumber = 0, pageSize = 20 } = useSelector((state: RootState) => state.app.table) || {};

    // H2: on parish change — clear list + reset page (never keep page N across parishes).
    // Also drop any open revoke-confirm dialog and its in-flight flag (it targets an
    // invitation id scoped to the parish that was selected when it opened) and any typed
    // search — a parish switch can silently re-derive activeTab (see below) without going
    // through goToTab, so searchText must be cleared here too, not just on manual tab
    // clicks, or a filter the box no longer visibly shows keeps being applied.
    useEffect(() => {
        if (!parishId) return;
        dispatch(clearParishMembers());
        setRevokeTarget(null);
        setRevoking(false);
        setSearchText('');
        if (pageNumber !== 0) {
            dispatch(updateTableState({ pageNumber: 0, pageSize, offset: 0 }));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps -- only react to parish switches
    }, [parishId, dispatch]);

    // Derived, not stored: if the user switches to a parish where they're no longer
    // CLERK+ while sitting on Invitations, `tabs` (below) drops that entry, and this
    // masks the pane back to Parish Members on the very same render — no effect-driven
    // one-frame lag where MUI Tabs holds a value absent from its own children, and no
    // separate reset path to keep in sync. `tab` itself is left untouched, so switching
    // back to a parish where they do have access resumes on Invitations automatically.
    const activeTab: TabValue = canManageMembers ? tab : 'parishmembers';

    useEffect(() => {
        if (!parishId || activeTab !== 'parishmembers') return;
        // page/pageSize are read fresh from state.app.table inside the thunk (see
        // parishMemberSlice.ts) rather than passed as arguments here — a value read from
        // this render's closure can be stale on the first render after the H2 reset above
        // (its dispatch lands one render later) or on a fresh mount carrying over a leftover
        // page number from a different screen. This effect is also what re-fetches on
        // ordinary pagination clicks (Table has no loadData prop here, unlike Reports.tsx),
        // so it must not be gated on pageNumber — it can just always fetch.
        dispatch(fetchParishMembers({ parishId, searchText: searchText || undefined }));
    }, [dispatch, parishId, activeTab, searchText, pageNumber, pageSize]);

    // Server-paginated (getMemberInvitations reads page/pageSize fresh from state.app.table),
    // so this effect re-fires on both pageNumber and pageSize — Next/Prev fetches the real next
    // page, and changing rows-per-page re-requests instead of silently doing nothing.
    useEffect(() => {
        if (!parishId || activeTab !== 'invitations') return;
        dispatch(getMemberInvitations({ parishId, status: 'PENDING' }));
    }, [dispatch, parishId, activeTab, pageNumber, pageSize]);

    const goToTab = useCallback(
        (v: TabValue) => {
            setTab(v);
            setSearchText('');
            // Members and Invitations share the global table-pagination slice (see
            // StyledTable) — reset it on every switch so a page-2 members view doesn't
            // leave the invitations table sliced into emptiness, and vice versa.
            dispatch(updateTableState({ pageNumber: 0, pageSize, offset: 0 }));
        },
        [dispatch, pageSize]
    );

    const handleConfirmRevoke = useCallback(async () => {
        if (!parishId || !revokeTarget) return;
        setRevoking(true);
        try {
            await dispatch(revokeMemberInvitationThunk({ parishId, invitationId: revokeTarget.id })).unwrap();
            setRevokeTarget(null);
        } catch {
            // Error toast already shown by the thunk; keep the dialog open so the user can retry.
        } finally {
            setRevoking(false);
        }
    }, [parishId, revokeTarget, dispatch]);

    const invitationColumns = useMemo(
        () => [
            ...baseInvitationColumns,
            {
                id: 'action',
                label: '',
                render: ({ data }: { data?: { status?: string; id?: string; email?: string } }) => {
                    if (data?.status !== 'PENDING' || !data.id || !data.email) return null;
                    const invitationId = data.id;
                    const invitationEmail = data.email;
                    return (
                        <Tooltip title="Revoke invitation">
                            <IconButton
                                size="small"
                                onClick={(e: React.MouseEvent) => {
                                    e.stopPropagation();
                                    setRevokeTarget({ id: invitationId, email: invitationEmail });
                                }}
                            >
                                <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    );
                },
            },
        ],
        []
    );

    const invitationRows = useMemo(() => (invitations || []).map(formatInvitationRow), [invitations]);

    const filteredInvitationRows = useMemo(() => {
        if (!searchText) return invitationRows;
        const q = searchText.toLowerCase();
        return invitationRows.filter((row) => row.name?.toLowerCase().includes(q) || row.email?.toLowerCase().includes(q));
    }, [invitationRows, searchText]);

    const navigationHandler = (row: { userId?: string; uuid?: string; id?: string }) => {
        const memberId = row.userId || row.uuid || row.id;
        if (!memberId) return;
        navigate(routes.community.ParishonerDetails.replace(':uuid', memberId));
    };

    const handleChangeSearch = useMemo(
        () =>
            debounce((e: React.ChangeEvent<HTMLInputElement>) => {
                const { value } = e.target || {};
                setSearchText(value || '');
                dispatch(updateTableState({ pageNumber: 0, pageSize, offset: 0 }));
            }, 700),
        [dispatch, pageSize]
    );

    // Pending Invitations tab is permanently hidden: Invite New Member now provisions the
    // account immediately (POST .../member-invitations creates the membership on the spot), so
    // this list can never show anything. Left the thunk/service/effect below in place, unused,
    // in case token invitations are ever reintroduced.
    const tabs: { value: TabValue; label: string }[] = [{ value: 'parishmembers', label: 'Parish Members' }];

    const isLoading = activeTab === 'parishmembers' ? loading : invitationsLoading;

    return (
        <StyledContainer style={{ borderRadius: 12 }}>
            <MuiLoader loading={isLoading} absolute />
            <div style={{ padding: 22 }}>
                <Header />
            </div>
            <StyledActionContainer>
                <StyledInnerContainer>
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            paddingLeft: 22,
                            flexDirection: 'row',
                            justifyContent: 'space-around',
                        }}
                    >
                        <Tab tabs={tabs} tabValue={activeTab} setTabValue={goToTab} />
                    </div>
                    {/* Invite New Member opens InviteMemberModal (instant parishioner provisioning). */}
                    {canManageMembers && (
                        <Button
                            sx={{ m: 2, width: { xs: '50%', sm: '30%', lg: '20%' }, fontSize: { xs: 14, md: 18 } }}
                            title="Invite New Member"
                            onClick={() => setInviteMemberModal(true)}
                        />
                    )}
                    <div style={{ padding: 16 }}>
                        {/* TextInput is an uncontrolled MUI field (no value prop), so clearing
                            searchText in state doesn't touch the visible DOM value — remount on
                            whichever of tab/parish just reset it (both clear searchText above) to
                            actually clear the box. */}
                        <TextInput key={`${parishId}-${activeTab}`} onChange={handleChangeSearch} placeholder="Search name or email" />
                    </div>

                    {error && !isLoading && activeTab === 'parishmembers' && (
                        <Alert severity="error" sx={{ mx: 2, mb: 2 }}>
                            {error}
                        </Alert>
                    )}

                    {!parishId ? (
                        <Stack justifyContent="center" alignItems="center" minHeight="300px">
                            <Typography>Select a parish to view members.</Typography>
                        </Stack>
                    ) : activeTab === 'parishmembers' ? (
                        <Table
                            unStyled
                            navigationHandler={navigationHandler}
                            columns={columns}
                            data={parishList}
                            loading={loading}
                            totalCount={total}
                            serverSidePagination
                        />
                    ) : (
                        <>
                            {!invitationsLoading && filteredInvitationRows.length === 0 ? (
                                <Stack sx={{ px: 2, pb: 2 }}>
                                    <Typography color="text.secondary">
                                        No pending invitations. New members are created immediately when you use Invite New Member.
                                    </Typography>
                                </Stack>
                            ) : null}
                            <Table
                                unStyled
                                navigationHandler={() => {}}
                                columns={invitationColumns}
                                data={filteredInvitationRows}
                                loading={invitationsLoading}
                                // filteredInvitationRows.length while searching — search has no server-side
                                // param on this endpoint, so it only narrows the current page's rows, not the
                                // full backend total. The real captured total otherwise.
                                totalCount={searchText ? filteredInvitationRows.length : invitationsTotal}
                                serverSidePagination
                            />
                        </>
                    )}
                </StyledInnerContainer>
            </StyledActionContainer>
            <InviteMemberModal
                isOpen={inviteMemberModal}
                onClose={() => setInviteMemberModal(false)}
                onInvited={() => {
                    if (!parishId) return;
                    dispatch(fetchParishMembers({ parishId, searchText: searchText || undefined }));
                    if (activeTab === 'invitations') {
                        dispatch(getMemberInvitations({ parishId, status: 'PENDING' }));
                    }
                }}
            />
            <DeleteModal
                isOpen={!!revokeTarget}
                onClose={() => {
                    if (!revoking) setRevokeTarget(null);
                }}
                title="Revoke invitation"
                description={`Revoke the invitation for ${revokeTarget?.email}? They won't be able to use this invite link anymore.`}
                loading={revoking}
                onDelete={handleConfirmRevoke}
            />
        </StyledContainer>
    );
};

export default Community;
